import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchPosEarningConfig,
    savePosEarningConfig,
} from "../../api/requests/pos-reconciliation-requests";

/* ============================================================================
 * Komisyon Hakediş Oranları — firma × POS sağlayıcı.
 *
 * Görünüm, Sanal POS Ayarları'ndaki entegrasyon kartlarıyla aynı desendedir:
 * her sağlayıcı bir kart, oranı girilenlerde üst şerit yeşil ve rozet oranı
 * gösterir. Bir firma birden fazla POS ile anlaşabildiği için tüm sağlayıcılar
 * listelenir; oran girilmeyen sağlayıcıda POS Rapor'un "Komisyon Kazancı"
 * kolonu "—" kalır (sıfır kazanç varsayılmaz).
 * ========================================================================== */

const NAVY = "#0A316E";
const GREEN = "#2f9e44";

/** %0,20 → "binde 2" · %0,05 → "binde 0,5" */
const perMilleLabel = (percent) => {
    const value = Number(percent);
    if (!Number.isFinite(value) || value <= 0) return null;
    const perMille = value * 10;
    const text = Number.isInteger(perMille) ? perMille : perMille.toFixed(2).replace(/\.?0+$/, "");
    return `binde ${String(text).replace(".", ",")}`;
};

const fmtPercent = (value) => String(value).replace(".", ",");

export default function CompanyEarningSettings({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [providers, setProviders] = useState([]);
    // { [virtualPos]: "0.30" } — boş string "oran girilmemiş" demektir
    const [rates, setRates] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchPosEarningConfig(companyId);
            if (res?.statusCode === 200 && Array.isArray(res.data)) {
                setProviders(res.data.map((p) => ({ key: p.virtualPos, label: p.providerLabel || p.virtualPos })));
                const map = {};
                res.data.forEach((p) => {
                    map[p.virtualPos] = p.earningPercent != null ? String(p.earningPercent) : "";
                });
                setRates(map);
            } else {
                toast.error(res?.message || "Hakediş oranları alınamadı");
            }
        } catch (e) {
            toast.error("Hakediş oranları yüklenirken hata oluştu");
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const items = providers.map((p) => ({
                virtualPos: p.key,
                // Boş bırakılan alan kaydı siler; 0 ise "kazanç yok" olarak kaydedilir.
                earningPercent: rates[p.key] === "" || rates[p.key] == null ? null : Number(rates[p.key]),
            }));
            const res = await savePosEarningConfig(companyId, items);
            if (res?.statusCode === 200) toast.success("Komisyon hakediş oranları kaydedildi");
            else toast.error(res?.message || "Hakediş oranları kaydedilemedi");
        } catch (e) {
            toast.error("Kaydederken hata oluştu");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted p-3">
                <Spinner size="sm" /> <span>Hakediş oranları yükleniyor...</span>
            </div>
        );
    }

    const definedCount = providers.filter((p) => rates[p.key] !== "" && rates[p.key] != null).length;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                <h5 className="text-muted mb-0">Komisyon Hakediş Oranları</h5>
                <Button
                    size="sm"
                    style={{ backgroundColor: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999 }}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                </Button>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Bu firmanın her POS sağlayıcısı için hakediş oranını yüzde olarak girin
                (binde 2 → <strong>0,20</strong>). POS Rapor'daki <em>Komisyon Kazancı</em> kolonu
                bu orandan hesaplanır: (işlem tutarı − iade) × oran. Boş bırakılan sağlayıcıda
                kolon "—" görünür. {definedCount > 0 && `Şu an ${definedCount} sağlayıcıda oran tanımlı.`}
            </p>

            {/* Sanal POS entegrasyon kartlarıyla aynı grid deseni */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "1rem",
                }}
            >
                {providers.map((p) => {
                    const value = rates[p.key];
                    const hasRate = value !== "" && value != null;
                    const hint = perMilleLabel(value);
                    return (
                        <div
                            key={p.key}
                            className="h-100 d-flex flex-column border-0 shadow-sm"
                            style={{
                                borderRadius: 16,
                                overflow: "hidden",
                                background: hasRate
                                    ? "linear-gradient(180deg, rgba(47,158,68,.06), rgba(47,158,68,0))"
                                    : "#fff",
                            }}
                        >
                            <div
                                style={{
                                    height: 4,
                                    background: hasRate ? `linear-gradient(90deg, ${GREEN}, #51cf66)` : "#e7eaf0",
                                }}
                            />

                            <div className="p-3 d-flex flex-column flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div
                                        className="d-flex align-items-center justify-content-center bg-white shadow-sm"
                                        style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0 }}
                                    >
                                        <i className="bx bx-wallet" style={{ fontSize: 24, color: NAVY }} />
                                    </div>
                                    <span
                                        className="d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            color: hasRate ? GREEN : "#5c6a86",
                                            backgroundColor: hasRate ? "rgba(47,158,68,.12)" : "rgba(92,106,134,.12)",
                                            borderRadius: 999,
                                            padding: ".25rem .7rem",
                                            fontSize: 12,
                                        }}
                                    >
                                        {hasRate ? `%${fmtPercent(value)}` : "Tanımsız"}
                                    </span>
                                </div>

                                <h6 className="fw-bold mt-3 mb-1" style={{ color: "#1f2937" }}>
                                    {p.label}
                                </h6>
                                <p className="text-muted mb-3" style={{ fontSize: 13, lineHeight: 1.35 }}>
                                    Komisyon hakediş oranı
                                </p>

                                <div className="mt-auto">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">%</span>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step="0.01"
                                            value={value ?? ""}
                                            onChange={(e) =>
                                                setRates((prev) => ({ ...prev, [p.key]: e.target.value }))
                                            }
                                            placeholder="örn. 0,20"
                                        />
                                    </div>
                                    <div className="text-muted mt-1" style={{ fontSize: 11, minHeight: 16 }}>
                                        {hint || "oran girilmedi"}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
